/**
 * WatchtowerAlertsWidget Component
 *
 * Right column widget showing 3 most urgent deadlines.
 * Based on DASHBOARD_FUNCTIONAL_SPEC.md - Phase 2 implementation.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import {
  DeadlineSummary,
  PRIORITY_STYLES,
  TYPE_COLORS,
  formatShortDate,
  getDaysUntil,
} from '../deadlines/types';
import { Eye, Calendar, Clock, Loader2, AlertCircle, ChevronRight } from 'lucide-react';

interface GetUpcomingDeadlinesResponse {
  success: boolean;
  deadlines: Array<DeadlineSummary & { caseFileName?: string }>;
  totalCount: number;
  error?: string;
}

interface WatchtowerAlertsWidgetProps {
  activeCaseFileId?: string;
  limit?: number;
}

export function WatchtowerAlertsWidget({
  activeCaseFileId,
  limit = 3,
}: WatchtowerAlertsWidgetProps): JSX.Element {
  const navigate = useNavigate();
  const [deadlines, setDeadlines] = useState<Array<DeadlineSummary & { caseFileName?: string }>>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUpcoming = async () => {
      setLoading(true);
      setError(null);

      try {
        const getUpcomingDeadlines = httpsCallable<
          { caseFileId?: string; daysAhead?: number; limit?: number },
          GetUpcomingDeadlinesResponse
        >(functions, 'getUpcomingDeadlines');

        const result = await getUpcomingDeadlines({
          caseFileId: activeCaseFileId,
          daysAhead: 60,
          limit,
        });

        if (result.data.success) {
          setDeadlines(result.data.deadlines);
          setTotalCount(result.data.totalCount);
        } else {
          throw new Error(result.data.error || 'Failed to load deadlines');
        }
      } catch (err) {
        console.error('Error loading watchtower alerts:', err);
        const rawMessage = err instanceof Error ? err.message : '';
        if (rawMessage.toLowerCase().includes('not found') || rawMessage.toLowerCase().includes('does not have access')) {
          setError('Unable to load alerts for this care record.');
        } else {
          setError('Unable to load alerts. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUpcoming();
  }, [activeCaseFileId, limit]);

  const handleViewAll = () => {
    if (activeCaseFileId) {
      navigate(`/casefile/${activeCaseFileId}/deadlines`);
    }
  };

  const handleDeadlineClick = (deadline: DeadlineSummary) => {
    navigate(`/casefile/${deadline.caseFileId}/deadlines`);
  };

  // Get urgency color based on days remaining
  const getUrgencyColor = (deadline: DeadlineSummary) => {
    if (deadline.status === 'overdue') return 'text-red-600';
    if (deadline.status === 'due_soon') return 'text-amber-600';
    return 'text-muted';
  };

  // Format days remaining text
  const formatDaysRemaining = (dueDate: string): string => {
    const daysUntil = getDaysUntil(dueDate);
    if (daysUntil < 0) return `${Math.abs(daysUntil)}d overdue`;
    if (daysUntil === 0) return 'Today';
    if (daysUntil === 1) return 'Tomorrow';
    return `${daysUntil}d`;
  };

  return (
    <div className="card h-full">
      {/* Header */}
      <div className="card-header flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Eye size={18} className="text-evergreen" />
          <h3 className="font-semibold text-ink">Watchtower Alerts</h3>
        </div>
        <button
          onClick={handleViewAll}
          className="btn-ghost btn-sm text-evergreen hover:text-deep-forest flex items-center gap-1"
        >
          View All
          <ChevronRight size={16} />
        </button>
      </div>

      <div className="card-body">
        {/* Loading state */}
        {loading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 size={24} className="animate-spin text-evergreen" />
          </div>
        )}

        {/* Error state */}
        {!loading && error && (
          <div className="flex items-center gap-2 text-red-600 py-4">
            <AlertCircle size={18} />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && deadlines.length === 0 && (
          <div className="text-center py-8">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-soft-neutral flex items-center justify-center">
              <Calendar size={24} className="text-muted" />
            </div>
            <p className="text-muted text-sm">No upcoming deadlines</p>
            <p className="text-xs text-muted mt-1">You're all caught up!</p>
          </div>
        )}

        {/* Deadlines list */}
        {!loading && !error && deadlines.length > 0 && (
          <div className="space-y-2">
            {deadlines.map((deadline) => {
              const priorityStyle = PRIORITY_STYLES[deadline.priority];
              const typeColor = TYPE_COLORS[deadline.type];

              return (
                <button
                  key={deadline.deadlineId}
                  onClick={() => handleDeadlineClick(deadline)}
                  className={`w-full text-left p-3 rounded-lg border-l-4 ${priorityStyle.border} hover:bg-soft-neutral transition-colors`}
                >
                  <div className="flex items-start gap-3">
                    {/* Clock icon */}
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-soft-neutral flex items-center justify-center">
                      <Clock size={14} className="text-muted" />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-ink truncate">{deadline.title}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${typeColor}`}>
                          {deadline.type}
                        </span>
                        {deadline.caseFileName && (
                          <span className="text-xs text-muted truncate">{deadline.caseFileName}</span>
                        )}
                      </div>
                    </div>

                    {/* Days badge */}
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs text-muted">{formatShortDate(deadline.dueDate)}</p>
                      <p className={`text-xs font-semibold ${getUrgencyColor(deadline)}`}>
                        {formatDaysRemaining(deadline.dueDate)}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}

            {/* Show more indicator */}
            {totalCount > deadlines.length && (
              <button
                onClick={handleViewAll}
                className="w-full text-center text-sm text-evergreen hover:text-deep-forest pt-2"
              >
                +{totalCount - deadlines.length} more deadline{totalCount - deadlines.length > 1 ? 's' : ''}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default WatchtowerAlertsWidget;
